import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import * as Sentry from '@sentry/browser';
import format from 'string-template';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import WorkoutAssignment, { workoutAssignmentStatuses, WorkoutActions } from '../../../../../Model/WorkoutAssignment';
import GameplaySession from '../../../../../Model/GameplaySession';
import User from '../../../../../Model/User';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import useLogger from '../../../../../hooks/useLogger';
import { ActionType } from '../../../../pages/WorkoutsManager/Workouts/utils';
import ConfirmDialog from '../../../ConfirmDialog';
import LoadingOverlay from '../../../LoadingOverlay';
import WorkoutEditorModal from '../../../WorkoutEditorModal';
import config from '../../../../../config';
import { DateFormat } from '../../../../../utils/date';
import { CoachingActivity } from '../../../../../utils/log';
import MobileViewModal from '../MobileView';

import WorkoutAssignmentModal from './components/WorkoutAssignmentModal';
import WorkoutDeletionModal from './components/WorkoutDeletionModal';
import ProgramAssignmentModal from './components/ProgramAssignmentModal';
import EventContent from './components/EventContent';
import CalendarDayView from './components/CalendarDayView';
import {
  CalendarWrapper,
} from './styles';
import texts from './texts.json';
import './assets/css/styles.css';

const WORKOUT_ASSIGNMENT = 'workoutAssignment';
const DETAILS = 'details';

const WorkoutCalendar = ({
  user,
  userDoc,
}) => {
  const [workoutAssignments, setWorkoutAssignments] = useState({ docs: [] });
  const [showLoading, setShowLoading] = useState(true);
  const [showWorkoutAssignmentModal, setShowWorkoutAssignmentModal] = useState(false);
  const [showDeleteWorkoutModal, setShowDeleteWorkoutModal] = useState(false);
  const [showWorkoutDetailsPopup, setShowWorkoutDetailsPopup] = useState(false);
  const [showWorkoutEditorModal, setShowWorkoutEditorModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [futureLikeAssignments, setFutureLikeAssignments] = useState([]);
  const [copiedWorkout, setCopiedWorkout] = useState(null);
  const [showProgramAssignmentModal, setShowProgramAssignmentModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);
  const [mobileUrl, setMobileUrl] = useState('');

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const {
    externalCoachDoc: {
      id: coachId,
    },
  } = useContext(ExternalCoachContext);
  const {
    userId: loggedInUserId,
  } = useContext(LoggedInUserContext);
  const { logCoachingActivity } = useLogger();

  useEffect(() => {
    const loadWorkouts = async () => {
      const workoutCollection = await WorkoutAssignment.getWorkoutAssignmentsForUser(user);
      if (isComponentMountedRef.current) {
        setWorkoutAssignments(workoutCollection);
        setShowLoading(false);
      }
    };
    loadWorkouts();
  }, [
    user,
    isComponentMountedRef,
  ]);

  const refreshLastWorkoutAssignedDate = useCallback(async (id, startDate, action) => {
    try {
      const lastAssigned = userDoc.lastWorkoutAssignedDate
        ? moment.utc(userDoc.lastWorkoutAssignedDate.toDate())
        : null;

      if (!lastAssigned) {
        Sentry.captureMessage('Missing lastWorkoutAssignedDate during refresh.', {
          extra: {
            userId: user,
            workoutAssignmentId: id,
          },
        });
        return;
      }

      let newLastWorkoutAssignedDate = startDate;
      const start = moment.utc(startDate.toDate());

      // If the action is a deletion and the deleted workout's date is the same as the last assigned workout date,
      // OR if the action is a movement and the moved workout's date is before the last assigned workout date,
      // then fetch the updated last assigned workout date from the database.
      // This is to ensure that the last assigned date is always the most recent one.
      if (
        (action === WorkoutActions.DELETE && start.isSame(lastAssigned, 'day'))
        || (action === WorkoutActions.MOVE && start.isBefore(lastAssigned, 'day'))
      ) {
        newLastWorkoutAssignedDate = await WorkoutAssignment.getLastWorkoutAssignmentDate(user);
      }

      if ((action === WorkoutActions.DELETE && !start.isBefore(lastAssigned, 'day'))
        || (action === WorkoutActions.MOVE)) {
        await userDoc.updateLastWorkoutAssignedDate(newLastWorkoutAssignedDate);
      }
    } catch (error) {
      Sentry.captureMessage(error, {
        extra: {
          workoutAssignmentId: id,
        },
      });
    }
  }, [userDoc, user]);

  const deleteWorkoutAssignment = useCallback(async (workoutAssignment, closePopup) => {
    try {
      closePopup();
      const likeAssignments = workoutAssignments.docs.filter((doc) => (
        doc.name === workoutAssignment.name
        && moment(doc.startDate.toDate()).isSameOrAfter(workoutAssignment.startDate.toDate())
      ));
      const hasMoreLikeAssignments = likeAssignments.length > 1;
      // Show the deletion modal if the assignment is in a program or has similar assignments in the future
      if (workoutAssignment.programId || hasMoreLikeAssignments) {
        if (hasMoreLikeAssignments) {
          setFutureLikeAssignments(likeAssignments);
        }
        setSelectedWorkout(workoutAssignment);
        setShowDeleteWorkoutModal(true);
      } else {
        setShowLoading(true);
        if (workoutAssignment.status === workoutAssignmentStatuses.ASSIGNED) {
          logCoachingActivity(CoachingActivity.DELETED_WORKOUT_ASSIGNMENT, {
            workoutAssignmentId: workoutAssignment.id,
            clientId: workoutAssignment.user,
          });
          await workoutAssignment.delete();
          showToast(texts.deleteSuccess);
          await refreshLastWorkoutAssignedDate(workoutAssignment.id, workoutAssignment.startDate,
            WorkoutActions.DELETE);
        } else {
          showToast(texts.inProgressWorkout, { warning: true });
        }
        setShowLoading(false);
      }
    } catch (error) {
      const errMsg = format(texts.deleteError, { msg: error.message });
      showToast(errMsg, { error: true });
      Sentry.captureMessage(errMsg, {
        extra: {
          userId: user,
          workoutAssignmentId: workoutAssignment.id || 'undefined',
        },
      });
    }
  }, [
    workoutAssignments,
    logCoachingActivity,
    showToast,
    refreshLastWorkoutAssignedDate,
    user,
  ]);

  const copyWorkoutAssignment = useCallback(async (workout, closePopup) => {
    closePopup();
    setCopiedWorkout(workout);
  }, []);

  const onPasteWorkout = useCallback(async (dateStr) => {
    await WorkoutAssignment.assignWorkout(userDoc, copiedWorkout, {
      startDate: moment.utc(dateStr).startOf('day'),
      endDate: moment.utc(dateStr).endOf('day'),
      coachId,
    });
    logCoachingActivity(CoachingActivity.ASSIGNED_WORKOUT, {
      workoutId: copiedWorkout.id,
      clientId: userDoc.id,
    });
    showToast(texts.pasteSuccess);
    if (isComponentMountedRef.current) {
      setShowLoading(false);
    }
  }, [
    isComponentMountedRef,
    copiedWorkout,
    showToast,
    userDoc,
    coachId,
    logCoachingActivity,
  ]);

  const onWorkoutAssignmentClick = useCallback((workoutAssignment) => {
    setMobileUrl(`
      ${config.mobileAppURL}/u/${user}/${WORKOUT_ASSIGNMENT}/${workoutAssignment.id}/${DETAILS}?coachId=${coachId}
    `);
    setSelectedWorkout(workoutAssignment);
    setShowWorkoutDetailsPopup(true);
  }, [
    user,
    coachId,
  ]);

  const editWorkoutAssignment = useCallback(async (workoutAssignment, closePopup) => {
    closePopup();
    setSelectedWorkout(workoutAssignment);
    setShowWorkoutEditorModal(true);
  }, []);

  /**
   * Marks the selected workout assignment as done.
   * This will force finish the gameplay session and mark the workout assignment as partially completed.
   * The feed view and streak documents will get automatically updated with the latest data after the update.
   */
  const onMarkAsDone = useCallback(async () => {
    try {
      setShowLoading(true);
      const {
        id: workoutAssignmentId,
        user: userId,
      } = selectedWorkout;

      const gameplayCol = await GameplaySession.getLatestGameplaySessionCollection(userId, workoutAssignmentId);
      if (!gameplayCol.hasDocs) {
        throw new Error(texts.noGameplay);
      }
      const gameplaySession = gameplayCol.docs[0];
      await gameplaySession.forceFinish();
      await selectedWorkout.forceCompleteAssignment(gameplaySession.ref.path, loggedInUserId);
      if (isComponentMountedRef.current) {
        setSelectedWorkout(null);
        setShowCompletionModal(false);
        setShowLoading(false);
      }
      showToast(texts.completionSuccess);
    } catch (err) {
      showToast(format(texts.completionError, { err }), { error: true });
    }
  }, [
    isComponentMountedRef,
    showToast,
    loggedInUserId,
    selectedWorkout,
  ]);

  const onMarkAsDoneClick = useCallback((workoutAssignment, closePopup) => {
    closePopup();
    setSelectedWorkout(workoutAssignment);
    setShowCompletionModal(true);
  }, []);

  const renderEventContent = useCallback((eventInfo) => {
    const workoutAssignment = workoutAssignments.docs.find((w) => w.id === eventInfo.event.id);
    if (!workoutAssignment) return null;
    return (
      <EventContent
        workoutAssignment={workoutAssignment}
        onWorkoutAssignmentClick={onWorkoutAssignmentClick}
        editWorkoutAssignment={editWorkoutAssignment}
        deleteWorkoutAssignment={deleteWorkoutAssignment}
        copyWorkoutAssignment={copyWorkoutAssignment}
        onMarkAsDone={onMarkAsDoneClick}
      />
    );
  }, [
    deleteWorkoutAssignment,
    copyWorkoutAssignment,
    workoutAssignments.docs,
    onWorkoutAssignmentClick,
    editWorkoutAssignment,
    onMarkAsDoneClick,
  ]);

  const workoutCompletionStatus = useMemo(() => (
    workoutAssignments.docs.reduce((acc, doc) => {
      acc[doc.id] = doc?.isCompleted === true;
      return acc;
    }, {})
  ), [workoutAssignments.docs]);

  const transformData = useCallback((workoutAssignment) => ({
    id: workoutAssignment.id,
    title: workoutAssignment.name,
    start: workoutAssignment.momentStartDate.toDate(),
    allDay: true,
    editable: !workoutCompletionStatus[workoutAssignment.id],
    eventDurationEditable: false,
  }), [workoutCompletionStatus]);

  const onDateClick = useCallback(({ dateStr, isProgram = false }) => {
    setSelectedDate(dateStr);
    if (isProgram) {
      setShowProgramAssignmentModal(true);
    } else {
      setShowWorkoutAssignmentModal(true);
    }
  }, []);

  const handleCellMount = useCallback(({ el, date }) => {
    const cellDiv = el;
    cellDiv.onmouseenter = () => setHoverDate(date);
    cellDiv.onmouseleave = () => setHoverDate(null);
  }, []);

  const updateWorkoutAssignmentDates = useCallback(async (id, eventStartDate) => {
    setShowLoading(true);
    const workoutAssigmentDoc = workoutAssignments.docs.find((w) => w.id === id);
    if (workoutAssigmentDoc) {
      await workoutAssigmentDoc.ref.update({
        startDate: moment.utc(eventStartDate).startOf('day').toDate(),
        endDate: moment.utc(eventStartDate).endOf('day').toDate(),
      });

      const updatedDocSnap = await workoutAssigmentDoc.ref.get();

      await refreshLastWorkoutAssignedDate(workoutAssigmentDoc.id, updatedDocSnap.data().startDate,
        WorkoutActions.MOVE);
    }
    if (isComponentMountedRef.current) {
      showToast(texts.workoutUpdated);
      setShowLoading(false);
    }
  }, [
    workoutAssignments.docs,
    showToast,
    isComponentMountedRef,
    refreshLastWorkoutAssignedDate,
  ]);

  const onModalClose = useCallback(() => {
    setShowDeleteWorkoutModal(false);
    setFutureLikeAssignments([]);
  }, []);

  return (
    <>
      {showWorkoutAssignmentModal && (
        <WorkoutAssignmentModal
          showModal={showWorkoutAssignmentModal}
          onClose={() => setShowWorkoutAssignmentModal(false)}
          userDoc={userDoc}
          selectedDate={selectedDate}
        />
      )}
      {showDeleteWorkoutModal && (
        <WorkoutDeletionModal
          showModal={showDeleteWorkoutModal}
          onClose={onModalClose}
          selectedWorkout={selectedWorkout}
          workoutAssignments={workoutAssignments}
          futureLikeAssignments={futureLikeAssignments}
          refreshLastWorkoutAssignedDate={refreshLastWorkoutAssignedDate}
          action={WorkoutActions.DELETE}
        />
      )}
      {showProgramAssignmentModal && (
        <ProgramAssignmentModal
          showModal={showProgramAssignmentModal}
          onClose={() => setShowProgramAssignmentModal(false)}
          userDoc={userDoc}
          selectedDate={selectedDate}
        />
      )}

      {showWorkoutEditorModal && (
        <WorkoutEditorModal
          showModal={showWorkoutEditorModal}
          workoutAssignment={selectedWorkout}
          editorAction={ActionType.UPDATE_WORKOUT_ASSIGNMENT}
          onSave={() => setShowWorkoutEditorModal(false)}
          onClose={() => setShowWorkoutEditorModal(false)}
        />
      )}
      <ConfirmDialog
        isOpen={showCompletionModal}
        onConfirm={onMarkAsDone}
        onCancel={() => setShowCompletionModal(false)}
        dialogTexts={{
          title: texts.warningModal.title,
          content: format(texts.warningModal.description, {
            workout: selectedWorkout?.name,
            startedOn: moment(selectedWorkout?.workoutStartedDate?.toDate())
              ?.format(DateFormat.DATE_FORMAT_COMMA_TH),
          }),
        }}
      />
      <CalendarWrapper>
        <FullCalendar
          buttonText={{
            today: texts.today,
          }}
          headerToolbar={{
            right: 'prev,today,next',
          }}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          dayCellContent={(dayData) => (
            <CalendarDayView
              showPasteButton={!!copiedWorkout}
              selectedDate={hoverDate}
              dayData={dayData}
              onWorkoutAssignmentClick={(date) => onDateClick({ dateStr: date })}
              onProgramAssignmentClick={(date) => onDateClick({ dateStr: date, isProgram: true })}
              onWorkoutPasteClick={(date) => {
                onPasteWorkout(date);
              }}
            />
          )}
          events={workoutAssignments.docs.slice()}
          eventContent={renderEventContent}
          eventDataTransform={transformData}
          dayCellDidMount={handleCellMount}
          selectable
          eventDrop={
            (info) => updateWorkoutAssignmentDates(info.event.id, info.event.startStr)
          }
          eventReceive={
            (info) => updateWorkoutAssignmentDates(info.event.id, info.event.startStr)
          }
        />
      </CalendarWrapper>
      {showWorkoutDetailsPopup && (
        <MobileViewModal
          showModal={showWorkoutDetailsPopup}
          onClose={() => setShowWorkoutDetailsPopup(false)}
          mobileUrl={mobileUrl}
        />
      )}
      <LoadingOverlay isLoading={showLoading} />
    </>
  );
};

WorkoutCalendar.propTypes = {
  userDoc: PropTypes.instanceOf(User),
  user: PropTypes.string.isRequired,
};

WorkoutCalendar.defaultProps = {
  userDoc: null,
};

export default compose(
  observer,
)(WorkoutCalendar);
